import { Component, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { NgxSpinnerService } from "ngx-spinner";
import { ConfirmationService } from "primeng/api";
import { NotificationTypes } from "src/app/app.enums";
import { ConsignmentService, GalleryConsignmentProduct } from "src/app/Services/consignment.service";
import { NotificationsService } from "src/app/Services/shared/notifications.service";
import { StoreDetail, WoocomerceService, WoocommerceCredentials } from "src/app/Services/woocomerce-service/woocomerce.service";
import { SyncProductsComponent } from "../sync-products/sync-products.component";
import { TranslocoService } from "@jsverse/transloco";

@Component({
    selector: 'connect-woocommerce-account',
    templateUrl: './connect-woocomerce-account.component.html',
    inputs: ['woocommerceData', 'isSentByEditProfile'],
    standalone: false
})
export class ConnectWoocommerceAccountComponent implements OnInit {
  // Reference to child SyncProductsComponent
  @ViewChild(SyncProductsComponent) syncProductsComponent!: SyncProductsComponent;
  // Component state variables
  public isAddWoocommerceAccountDailogVisible: boolean = false;
  public wooCommerceSyncProducts: boolean = false;
  public WoocommerceAccount: StoreDetail[] = [];
  public woocommerceFormData!: WoocommerceCredentials;
  public woocommerceData!: WoocommerceCredentials;
  public Products: GalleryConsignmentProduct[] = [];
  public galleryProductsLoaded: boolean = false;
  public isSentByEditProfile: boolean = false;
  public storeName: string = '';
  public storeUrl: string = '';
  public isConnectWoocommerceSuccessDialogVisible: boolean = false;

  constructor(
    private ngxSpinnerService: NgxSpinnerService,
    private woocommerceService: WoocomerceService,
    private confirmationService: ConfirmationService,
    private notificationsService: NotificationsService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private transloco: TranslocoService,
  ) {
    // Initialize form data objects
    this.woocommerceFormData = new WoocommerceCredentials();
    this.woocommerceData = new WoocommerceCredentials();
  }

  ngOnInit(): void {
    // Subscribe to query parameters to handle success cases
    this.activatedRoute.queryParams.subscribe((params) => {
      if (params['success'] && params['success'] == 1) {
        // Clear success query param and show success dialog
        this.router.navigate([], {
          relativeTo: this.activatedRoute,
          queryParams: {},
          replaceUrl: true,
        });
        this.isConnectWoocommerceSuccessDialogVisible = true;
        this.getWoocommerceAccountDetails();
      } else {
        // Load Woocommerce accounts if not called from edit profile
        if (this.isSentByEditProfile == false) {
          this.getWoocommerceAccountDetails();
        }
      }
    });

    // Enable Woocommerce product sync by default
    this.wooCommerceSyncProducts = true;
  }

  /**
   * Shows the Woocommerce account dialog
   */
  public onShowWoocommerceAccountDailog() {
    this.isAddWoocommerceAccountDailogVisible = true;
  }

  /**
   * Closes the Woocommerce account dialog
   * @param $event - Boolean indicating whether to close the dialog
   */
  public onCloseWoocommerceAccountDailog($event: boolean) {
    this.isAddWoocommerceAccountDailogVisible = $event;
  }

  /**
   * Fetches Woocommerce account details from the server
   */
  public getWoocommerceAccountDetails() {
    this.ngxSpinnerService.show();
    this.woocommerceService.getWoocommerceAccountDetails().subscribe(
      (response) => {
        console.log(response);
        this.ngxSpinnerService.hide();
        if (response) {
          this.WoocommerceAccount = response;
        }
        console.log('WoocommerceAccount', this.WoocommerceAccount);
      },
      (error) => {
        this.ngxSpinnerService.hide();
        console.error('Error fetching Woocommerce account details:', error);
      }
    );
  }

  /**
   * Connects a Woocommerce account using the provided credentials
   */
  public connecWoocommerceAccount() {
    // Use either parent data (if storeUrl exists) or local form data
    const formData = this.woocommerceData.storeUrl
      ? this.woocommerceData
      : this.woocommerceFormData;

    this.ngxSpinnerService.show();
    console.log("woocommerceFormData", formData);

    this.woocommerceService.createWoocommerceAccount(formData).subscribe(
      (response) => {
        console.log('Woocommerce account created successfully:', response);
        if (response.Success) {
          // Redirect to the provided URL for OAuth flow
          window.location.href = response.ResponseData;
        }
        this.ngxSpinnerService.hide();
        this.isAddWoocommerceAccountDailogVisible = false;
      },
      (error) => {
        this.ngxSpinnerService.hide();
        console.error('Error adding Woocommerce account details:', error);
      }
    );
  }

  /**
   * Disconnects a Woocommerce account after confirmation
   * @param WoocommerceAccount - The account to disconnect
   */
  public disconnectWoocommerceAccount(WoocommerceAccount: StoreDetail): void {
  this.confirmationService.confirm({
    message: this.transloco.translate('connectWoocommerceAccount.disconnectConfirm'),
    header: this.transloco.translate('connectWoocommerceAccount.disconnectHeader'),
    icon: 'pi pi-exclamation-triangle',
    accept: () => {
      this.ngxSpinnerService.show();
      this.woocommerceService.disconnectWoocommerceAccount(WoocommerceAccount.Id).subscribe(
        (response) => {
          this.ngxSpinnerService.hide();

          if (response.Success) {
            this.getWoocommerceAccountDetails();

            if (this.syncProductsComponent) {
              // ✅ Clear selected store filter so disconnected store’s products are removed from view
              this.syncProductsComponent.selectedStoreFilter = null;
              // ✅ Reload only WooCommerce synced products
              this.syncProductsComponent.reloadPlatformProducts('WooCommerce');
            }

            // Step 3: Show success notification
            this.notificationsService.showNotification(
              this.transloco.translate('connectWoocommerceAccount.disconnectedTitle'),
              this.transloco.translate('connectWoocommerceAccount.disconnectedSuccess'),
              NotificationTypes.Success
            );
          }
        },
        (error) => {
          this.ngxSpinnerService.hide();
          console.error('Failed to disconnect WooCommerce account:', error);
        }
      );
    },
    reject: () => {
      console.log('WooCommerce account disconnection was cancelled.');
    }
  });
}


  /**
   * Closes the Woocommerce checkout success dialog
   */
  public closeWooCommerceCheckoutDialog(): void {
    this.isConnectWoocommerceSuccessDialogVisible = false;
  }
}